import {
  Injectable,
  LOCALE_ID,
  computed,
  inject,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, tap } from 'rxjs';

import { MediaQueryService } from '@core/breakpoints';
import { environment } from '@env';
import { syncWithLocalStorage } from '@core/helpers';
import { listenWebsocketEvents } from '@core/websocket';

import { AuthState, User } from '../users';
import { ItemsRainState } from '../items-rain/items-rain.state';

import {
  ApiResponse,
  Message,
  MessageType,
  Room,
  Sticker
} from './chat.entity';
import { ChatService } from './chat.service';

export const MAX_MESSAGES = 200;

@Injectable({
  providedIn: 'root'
})
export class ChatState {
  private readonly chatService = inject(ChatService);
  private readonly authState = inject(AuthState);
  private readonly itemsRainState = inject(ItemsRainState);
  private readonly localeId = inject(LOCALE_ID);
  private readonly mediaService = inject(MediaQueryService);

  public readonly wideScreen = toSignal(this.mediaService.mediaQuery('min', 'wideScreen'));
  public readonly messages = signal<Record<number, Message>>({});
  public readonly messageIds = signal<number[]>([]);
  public readonly pinnedMessage = signal<Message|null>(null);
  public readonly stickers = signal<Sticker[]>([]);
  public readonly rooms = signal<Record<number, Room>>({});
  public readonly localMutes = signal<number[]>([]);
  public readonly currentRoomId = signal<number|null>(null);
  public readonly taggedUser = signal<User|null>(null);
  public readonly isOpen = signal<boolean>(false);
  public readonly firstRun = signal<boolean>(true);

  public readonly messagesArray = computed(() => Object.values(this.messages()));
  public readonly availableStickers = computed(() => {
    const user = this.authState.user();

    if(!user) {
      return [];
    }

    return this.stickers().filter((sticker) => sticker.minLvl <= user.level);
  });

  public readonly filteredRooms = computed(() => {
    return Object.values(this.rooms())
      .filter(room => !environment.localesToHide.includes(room.locale));
  });

  public readonly currentRoom = computed(() => {
    const currentRoomId = this.currentRoomId();
    const rooms = this.rooms();

    if(null === currentRoomId || 0 === Object.values(rooms).length) {
      return null;
    }
 
    return rooms[currentRoomId];
  });

  private readonly wsEvents = listenWebsocketEvents({
    'chat.new_message': (data) => this.onWsNewMessage(data),
    'chat.messages_deleted': (data) => this.onWsMessagesDeleted(data),
    'chat.pin_message': (data) => this.onWsPinMessage(data),
    'chat.unpin_message': () => this.onWsUnpinMessage()
  });

  private readonly localStorageEffect = syncWithLocalStorage('chat', {
    isOpen: this.isOpen,
    firstRun: this.firstRun,
    currentRoomId: this.currentRoomId,
    localMutes: this.localMutes
  });

  constructor() {
    this.firstRun() && this.loadOptimizedDefaults();
  }

  loadMessages(roomId: number|null = null): Observable<ApiResponse.MessagesHistory> {
    let locale: string|null = this.localeId;

    if(null !== roomId) {
      this.messageIds.set([]);
      this.messages.set({});
      this.currentRoomId.set(roomId);
    }

    roomId = roomId || this.currentRoomId();

    if(null !== roomId) {
      locale = null;
    }

    return this.chatService.getMessages(locale, roomId).pipe(
      tap((response: ApiResponse.MessagesHistory) => {
        const messages: Record<number, Message> = {};
        const messageIds: number[] = [];
        const rooms: Record<number, Room> = {};

        response.messages.forEach((message: Message) => {
          messageIds.push(message.id);
          messages[message.id] = message;
        });

        response.rooms.forEach((room: Room) => {
          rooms[room.id] = room;
        });

        if (this.firstRun()) {
          this.currentRoomId.set(Object.values(rooms).find(item => item?.locale === this.localeId)?.id 
          || Object.values(rooms).find(item => item?.locale === 'en')?.id || null);
        } else {
          this.currentRoomId.set(response.currentRoom.id);
        }

        this.messages.set(messages);
        this.messageIds.set(messageIds);
        this.rooms.set(rooms);
        
        this.pinnedMessage.set(response.pinnedMessage);
        this.stickers.set(response.stickers);
        this.itemsRainState.setPeriod(response.itemsRainPeriod);
      })
    );
  }

  toggleIsOpen(isOpen: boolean|null = null) {
    this.isOpen.update((value) => isOpen !== null ? isOpen : !value);

    if(!this.isOpen()) {
      this.reset();
    }
  }

  sendMessage(
    type: MessageType,
    content: any
  ): Observable<ApiResponse.SendMessage> {
    return this.chatService.sendMessage(
      type,
      content,
      this.currentRoomId()!,
      this.taggedUser()?.id || null
    );
  }

  deleteMessage(messageId: number): Observable<void> {
    return this.chatService.deleteMessage(messageId);
  }

  pinMessage(messageId: number): Observable<void> {
    return this.chatService.pinMessage(messageId);
  }

  unpinMessage(messageId: number): Observable<void> {
    return this.chatService.unpinMessage(messageId);
  }

  setTaggedUser(user: User|null): void {
    this.taggedUser.set(user);
  }

  addLocalMutedUser(userId: number): void {
    this.localMutes.update((mutes) => [...mutes, userId]);
  }

  removeLocalMutedUser(userId: number): void {
    this.localMutes.update((value) => value.filter((row) => row !== userId));
  }

  reset(): void {
    this.messages.set({});
    this.messageIds.set([]);
    this.pinnedMessage.set(null);
    this.stickers.set([]);
    this.rooms.set({});
    this.localMutes.set([]);
    this.taggedUser.set(null);
  }

  private onWsNewMessage(message: Message): void {
    message.isAnimated = true;

    const messages = {
      ...this.messages(),
      [message.id]: message
    };

    const messageIds = [...this.messageIds(), message.id];

    if (messageIds.length > MAX_MESSAGES) {
      const firstMessageId = messageIds.shift()!;

      delete messages[firstMessageId];
    }

    this.messages.set(messages);
    this.messageIds.set(messageIds);
  }

  private onWsMessagesDeleted(messageIds: number[]): void {
    const messages = {...this.messages()};

    messageIds.forEach((id) => {
      delete messages[id];
    });

    this.messageIds.update((ids) => ids.filter((id) => !messageIds.includes(id)));
    this.messages.set(messages);
  }

  private onWsPinMessage(message: Message): void {
    this.pinnedMessage.set(message);
  }

  private onWsUnpinMessage(): void {
    this.pinnedMessage.set(null);
  }

  private loadOptimizedDefaults(): void {
    if (this.wideScreen()) {
      this.isOpen.set(true);
    }

    this.firstRun.set(false);
  }
}
